/**
 * @fileoverview User profile and membership management actions
 * @module UserActions
 */

import apiClient from "../utils/apiClient";
import queryString from "query-string";
import useSWR from "swr";

/**
 * @typedef {Object} SWRObject
 * @property {Object} data - The data returned by swr call
 * @property {boolean} isLoading - State of the swr call
 */
/**
 * Hook that returns profile data for the current user
 * @function
 * @name useProfile
 * @memberof module:UserActions
 * @returns {SWRObject} SWR Object containing user profile data
 */
export const useProfile = () => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data[0]);
  const { data, error, mutate } = useSWR("/accounts/me/", fetcher);

  return { data: data, isLoading: !error && !data, error, mutate };
};

/**
 * Updates the contact info for the current user
 * @function
 * @name updateProfile
 * @memberof module:UserActions
 * @param {Object} data - Object with the user contact info to update
 * @returns {Promise<void>}
 */
export const updateProfile = async (data) => {
  try {
    await apiClient.patch("/accounts/me/", data);
  } catch (err) {
    $log.error(err);
  }
};
/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */

/**
 * Hook that returns membership information
 * @function
 * @name useUsers
 * @memberof module:UserActions
 * @param {Object} params - Parameters for filtering membership data
 * @returns {SWRObject} SWR Object containing membership info
 */
export const useUsers = (params) => {
  const fetcher = (url) => apiClient.get(url).then((res) => res?.data);
  const { data, error, mutate } = useSWR(
    `/membership?${queryString.stringify(params)}`,
    fetcher
  );

  return { data: data, isLoading: !error && !data, error, mutate };
};

/**
 * Changes the user state/status
 * @function
 * @name toggleUser
 * @memberof module:UserActions
 * @param {string} id - User ID
 * @param {number} status - New status for the user
 * @returns {Promise<void>}
 */

export const toggleUser = async (id, status) => {
  await apiClient.patch(`membership/${id}`, {
    id,
    status,
  });
};

/**
 * Fetches the current user's profile data (non-hook version)
 * @function
 * @name getProfile
 * @memberof module:UserActions
 * @returns {Promise<Object[]>} User profile data array, or empty array if error occurs
 */
export const getProfile = async () => {
  try {
    const res = await apiClient.get("/accounts/me/");
    return res.data;
  } catch (err) {
    $log.error(err);
    return [];
  }
};

export const usePayoutMethods = () => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);

  const { data, error, mutate } = useSWR(
    `/membership/payout-methods/`,
    fetcher
  );

  return {
    data,
    isLoading: !error && !data,
    error,
    mutate,
  };
};

export const addPayoutMethod = async (data) => {
  try {
    const res = await apiClient.post("/membership/payout-methods/", data);
    return res.data;
  } catch (err) {
    $log.error(err);
    throw err;
  }
};
